import React from 'react';
import { ROLES } from 'components/rbac/type';
import routeConfig from 'routes/config';
import Dashboard from 'views/dashboard';
import APIKeys from 'views/developers/APIKeys';
import Transaction from 'views/transactions/details/index';
import PartnerStores from 'views/partnerStores';
import DispensaryTransactions from 'views/transactions/dispensaryList';
import PartnerTransactions from 'views/transactions/PartnerList';
import Login from 'views/auth/login';
import Stores from 'views/stores';
import NoAccessToStore from 'views/stores/noAccess';
import PaymentConfig from 'views/settings/config';
import InternalConfig from 'views/settings/internalConfig/';
import Tips from 'views/tips';
import DocumentManagement from 'views/applicationCenter/documentManagement';
import VirtualTerminals from 'views/payments/virtualTerminals';
import Marketplace from 'views/marketplace';
import SystemConfig from 'views/settings/systemConfig';

export interface IView {
  path: string;
  name: string; // skipcq: JS-0122
  icon?: string;
  component?: JSX.Element;
  layout?: string;
  hasSidebar?: boolean;
  hasTopbar?: boolean;
  roles?: Array<ROLES>;
  children?: Array<IView>;
  search?: string;
  title: string;
  requiredPermissions?: string[];
}

const commonViews: IView[] = [
  {
    path: routeConfig.AUTH.LOGIN.path,
    name: 'Login',
    component: <Login />,
    layout: routeConfig.AUTH.layout,
    hasSidebar: false,
    title: 'Log in',
  },
  {
    path: routeConfig.STORES.LIST.path,
    name: 'Stores',
    component: <Stores />,
    layout: routeConfig.STORES.layout,
    hasSidebar: false,
    title: 'Stores',
    children: [
      {
        path: routeConfig.STORES.LIST.path,
        name: 'Stores',
        component: <Stores />,
        layout: routeConfig.STORES.layout,
        hasSidebar: false,
        title: 'Stores',
      },
      {
        path: routeConfig.STORES.ERROR_ACCESS_DENIED.path,
        name: 'Stores',
        component: <NoAccessToStore />,
        layout: routeConfig.STORES.layout,
        hasSidebar: false,
        title: 'No access',
      },
    ],
  },
  {
    path: `${routeConfig.MARKETPLACE.marketplace.path}`,
    name: 'Marketplace',
    component: <Marketplace />,
    hasSidebar: true,
    title: 'Marketplace',
    layout: routeConfig.MARKETPLACE.layout,
  },
];

const partnerViews: IView[] = [
  ...commonViews,
  {
    path: routeConfig.PARTNER_STORES.stores.path,
    name: 'Stores',
    component: <PartnerStores />,
    layout: routeConfig.PARTNER_STORES.layout,
    hasSidebar: true,
    roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.ANALYST],
    title: 'Stores',
  },
  {
    path: `${routeConfig.PARTNER_TRANSACTIONS.transactions.path}`,
    name: 'Transactions',
    component: <PartnerTransactions />,
    layout: routeConfig.PARTNER_TRANSACTIONS.layout,
    hasSidebar: true,
    roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.ANALYST],
    title: 'Transactions',
  },
  {
    path: routeConfig.DEVELOPERS.API_KEYS.path,
    name: 'Developers',
    component: <APIKeys />,
    layout: routeConfig.DEVELOPERS.layout,
    hasSidebar: true,
    roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.DEVELOPER],
    title: 'Developers',
    children: [
      {
        path: routeConfig.DEVELOPERS.API_KEYS.path,
        name: 'API Keys',
        component: <APIKeys />,
        layout: routeConfig.DEVELOPERS.layout,
        hasSidebar: true,
        title: 'API Keys',
      },
      {
        path: routeConfig.DEVELOPERS.DOCUMENTATION.path,
        name: 'Documentation',
        hasSidebar: true,
        title: 'Documentation',
      },
    ],
  },
];

const dispensaryViews: IView[] = [
  ...commonViews,
  {
    path: routeConfig.ANALYTICS.HOME.path,
    name: 'Analytics',
    component: <Dashboard />,
    layout: routeConfig.ANALYTICS.layout,
    hasSidebar: true,
    roles: [],
    title: 'Analytics',
  },
  {
    path: `${routeConfig.PAYMENTS.transactions.path}`,
    name: 'Payments',
    component: <DispensaryTransactions />,
    layout: routeConfig.PAYMENTS.layout,
    hasSidebar: true,
    hasTopbar: true,
    roles: [ROLES.ADMIN, ROLES.MANAGER, ROLES.ANALYST],
    title: 'Payments',
    children: [
      {
        path: `${routeConfig.PAYMENTS.transactions.path}`,
        name: 'Transactions',
        component: <DispensaryTransactions />,
        layout: routeConfig.PAYMENTS.layout,
        hasSidebar: true,
        search: '?tab=summary',
        title: 'Transactions',
      },
      {
        path: `${routeConfig.PAYMENTS.tips.path}`,
        name: 'Tips',
        component: <Tips />,
        layout: routeConfig.PAYMENTS.layout,
        hasSidebar: true,
        search: '?tab=summary',
        title: 'Tips',
      },
      {
        path: `${routeConfig.PAYMENTS.transactions.path}/:transactionId`,
        name: 'Transaction Details',
        component: <Transaction />,
        layout: routeConfig.PAYMENTS.layout,
        hasSidebar: false,
        title: 'Transaction Details',
      },
    ],
  },
  {
    path: `${routeConfig.APPLICATION_CENTER.document_management.path}`,
    name: 'Application Center',
    component: <DocumentManagement />,
    layout: routeConfig.APPLICATION_CENTER.layout,
    hasSidebar: true,
    title: 'Application Center',
    children: [
      {
        path: `${routeConfig.APPLICATION_CENTER.document_management.path}`,
        name: 'Document Management',
        component: <DocumentManagement />,
        layout: routeConfig.APPLICATION_CENTER.layout,
        hasSidebar: true,
        title: 'Document Management',
      },
      {
        path: `${routeConfig.APPLICATION_CENTER.one_app.path}`,
        name: 'One App',
        component: <></>,
        layout: routeConfig.APPLICATION_CENTER.layout,
        hasSidebar: true,
        title: 'One App',
      },
    ],
  },
  {
    path: routeConfig.VIRTUAL_TERMINALS.checkout.path,
    name: 'Virtual Terminal',
    component: <VirtualTerminals />,
    layout: routeConfig.VIRTUAL_TERMINALS.layout,
    hasSidebar: true,
    hasTopbar: true,
    roles: [],
    title: 'Virtual Terminal',
  },
  {
    path: routeConfig.SETTINGS.ORGANISATION.path,
    name: 'Settings',
    layout: routeConfig.SETTINGS.layout,
    hasSidebar: true,
    title: 'Settings',
    children: [
      {
        path: routeConfig.SETTINGS.CONFIGURATION.path,
        name: 'Payment configuration',
        component: <PaymentConfig />,
        layout: routeConfig.SETTINGS.layout,
        hasSidebar: true,
        title: 'Payment Configuration',
      },
      {
        path: routeConfig.SETTINGS.LOCATION_CONFIG.path,
        name: 'System configurations',
        // component: <LocationConfiguration />,
        component: <SystemConfig />,
        layout: routeConfig.SETTINGS.layout,
        hasSidebar: true,
        title: 'System Configurations',
      },
      {
        path: routeConfig.SETTINGS.INTERNAL_CONFIGURATION.path,
        name: 'Internal Configuration',
        component: <InternalConfig />,
        layout: routeConfig.SETTINGS.layout,
        hasSidebar: true,
        title: 'Internal Configuration',
        requiredPermissions: [
          'msotreezpay::admin',
          'msotreezpay::readAdminEntityProcessorConfiguration',
        ],
      },
    ],
  },
];

export default {
  partner: partnerViews,
  dispensary: dispensaryViews
};